<template>
  <div class="space-y-2">
    <RouterLink
      v-if="permissions.statistics"
      :to="{ name: 'statistics' }"
      :class="itemClass('statistics')"
      @click="handleClose"
    >
      <IconChartBar class="w-5 h-5" />
      <span v-translate="'nav.statistics'"></span>
    </RouterLink>

    <RouterLink
      v-if="permissions.telemetry"
      :to="{ name: 'telemetry' }"
      :class="itemClass('telemetry')"
      @click="handleClose"
    >
      <IconSignal class="w-5 h-5" />
      <span v-translate="'nav.telemetry'"></span>
    </RouterLink>

    <RouterLink
      v-if="permissions.applications"
      :to="{ name: 'applications' }"
      :class="itemClass('applications')"
      @click="handleClose"
    >
      <IconWindow class="w-5 h-5" />
      <span v-translate="'nav.applications'"></span>
    </RouterLink>

    <RouterLink
      v-if="permissions.users"
      :to="{ name: 'users' }"
      :class="itemClass(['users', 'user-edit', 'user-create'])"
      @click="handleClose"
    >
      <IconUsers class="w-5 h-5" />
      <span v-translate="'nav.users'"></span>
    </RouterLink>

    <RouterLink
      v-if="permissions.organizations"
      :to="{ name: 'organizations' }"
      :class="
        itemClass([
          'organizations',
          'organizations-create',
          'organizations-details',
          'organizations-details-applications',
          'organizations-details-users',
          'organizations-details-subs'
        ])
      "
      @click="handleClose"
    >
      <IconBook class="w-5 h-5" />
      <span v-translate="'nav.organizations'"></span>
    </RouterLink>

    <RouterLink v-if="permissions.audits" :to="{ name: 'audits' }" :class="itemClass('audits')" @click="handleClose">
      <IconDatabase class="w-5 h-5" />
      <span v-translate="'nav.audits'"></span>
    </RouterLink>

    <button v-if="permissions.remits" type="button" :class="itemClass()" @click="handleNavigateCustomersRemit">
      <div class="flex min-w-[1.25rem] items-center justify-center">
        <IconBankNote class="h-5 w-5" />
      </div>
      <span v-translate="'nav.remit'"></span>
    </button>

    <RouterLink
      v-if="permissions.vehicleRegistry"
      :to="{ name: 'vehicle-registry' }"
      :class="itemClass('vehicle-registry')"
      @click="handleClose"
    >
      <IconCar class="w-5 h-5" />
      <span v-translate="'nav.vehicleRegistry'"></span>
    </RouterLink>

    <RouterLink
      v-if="permissions.manualApproval"
      :to="{ name: 'manual-approval' }"
      :class="itemClass('manual-approval')"
      @click="handleClose"
    >
      <IconCheckCircle class="w-5 h-5" />
      <span v-translate="'nav.manualApproval'"></span>
    </RouterLink>

    <RouterLink
      v-if="permissions.camerasOverview"
      :to="{ name: 'cameras-overview' }"
      :class="itemClass('cameras-overview')"
      @click="handleClose"
    >
      <IconCamera class="w-5 h-5" />
      <span v-translate="'nav.camerasOverview'"></span>
    </RouterLink>

    <RouterLink
      v-if="permissions.trafficControl"
      :to="{ name: 'traffic-control' }"
      :class="itemClass('traffic-control')"
      @click="handleClose"
    >
      <IconShield class="w-5 h-5" />
      <span v-translate="'nav.trafficControl'"></span>
    </RouterLink>

    <RouterLink v-if="permissions.system" :to="{ name: 'system' }" :class="itemClass('system')" @click="handleClose">
      <IconServer class="w-5 h-5" />
      <span v-translate="'nav.system'"></span>
    </RouterLink>

    <button v-if="canAccessYouParkCustomer" type="button" :class="itemClass()" @click="handleNavigateCustomers">
      <IconBriefcase class="w-5 h-5" />
      <span v-translate="'nav.youparkCustomers'"></span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import type { ViewType } from "@core/composables/state/useNavigation";
import IconBankNote from "@core/icons/IconBankNote.vue";
import IconBook from "@core/icons/IconBook.vue";
import IconBriefcase from "@core/icons/IconBriefcase.vue";
import IconCamera from "@core/icons/IconCamera.vue";
import IconCar from "@core/icons/IconCar.vue";
import IconChartBar from "@core/icons/IconChartBar.vue";
import IconCheckCircle from "@core/icons/IconCheckCircle.vue";
import IconDatabase from "@core/icons/IconDatabase.vue";
import IconServer from "@core/icons/IconServer.vue";
import IconShield from "@core/icons/IconShield.vue";
import IconSignal from "@core/icons/IconSignal.vue";
import IconUsers from "@core/icons/IconUsers.vue";
import IconWindow from "@core/icons/IconWindow.vue";
import { RouterLink } from "vue-router";

type AdminPermissions = {
  statistics: boolean;
  telemetry: boolean;
  applications: boolean;
  users: boolean;
  organizations: boolean;
  audits: boolean;
  remits: boolean;
  vehicleRegistry: boolean;
  manualApproval: boolean;
  trafficControl: boolean;
  system: boolean;
  camerasOverview: boolean;
};

const props = defineProps<{
  permissions: AdminPermissions;
  canAccessYouParkCustomer: boolean;
  activeView: ViewType;
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "navigate-customers"): void;
  (e: "navigate-customers-remit"): void;
}>();

const handleClose = () => {
  emit("close");
};

const handleNavigateCustomers = () => {
  handleClose();
  emit("navigate-customers");
};

const handleNavigateCustomersRemit = () => {
  handleClose();
  emit("navigate-customers-remit");
};

const baseClass = "w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 text-left";
const inactiveClass =
  "text-gray-600 dark:text-white hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-white";
const activeClass = "bg-green-500 text-white font-medium shadow-sm";

const itemClass = (view?: ViewType | ViewType[]) => {
  const views = Array.isArray(view) ? view : view ? [view] : [];
  return computed(() =>
    views.length > 0 && views.includes(props.activeView)
      ? `${baseClass} ${activeClass}`
      : `${baseClass} ${inactiveClass}`
  ).value;
};
</script>
